import { useEffect } from 'react';

/**
 * AnalyticsManager Component
 * Dynamically injects all tracking pixels and analytics scripts
 * 
 * Supports:
 * - Google Analytics 4 (GA4)
 * - Google Tag Manager (GTM)
 * - Facebook Pixel
 * - Google Ads
 * - LinkedIn Insight Tag
 * - Custom head/body scripts
 */
function AnalyticsManager({ settings }) {
  useEffect(() => {
    if (!settings) return;

    // Google Tag Manager (should be loaded first)
    if (settings.google_tag_manager_id) {
      loadGTM(settings.google_tag_manager_id);
    }

    // Google Analytics 4
    if (settings.google_analytics_id) {
      loadGA4(settings.google_analytics_id);
    }

    // Facebook Pixel
    if (settings.facebook_pixel_id) {
      loadFacebookPixel(settings.facebook_pixel_id);
    }

    // Google Ads
    if (settings.google_ads_id) {
      loadGoogleAds(settings.google_ads_id);
    }

    // LinkedIn Insight Tag
    if (settings.linkedin_partner_id) {
      loadLinkedInInsight(settings.linkedin_partner_id);
    }

    // Custom Scripts
    if (settings.custom_head_scripts) {
      injectCustomScript(settings.custom_head_scripts, 'head');
    }
    if (settings.custom_body_scripts) {
      injectCustomScript(settings.custom_body_scripts, 'body');
    }

  }, [settings]);

  return null;
}

// Google Tag Manager
function loadGTM(gtmId) {
  if (window.gtmLoaded) return;
  window.gtmLoaded = true;

  // GTM script
  const script = document.createElement('script');
  script.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');
  `;
  document.head.appendChild(script);

  // GTM noscript iframe
  const noscript = document.createElement('noscript');
  noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
  document.body.insertBefore(noscript, document.body.firstChild);

  console.log('[Analytics] GTM loaded:', gtmId);
}

// Google Analytics 4
function loadGA4(measurementId) {
  if (window.ga4Loaded) return;
  window.ga4Loaded = true;

  // Load gtag.js
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag(){window.dataLayer.push(arguments);}
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', measurementId, {
    page_title: document.title,
    page_location: window.location.href
  });

  console.log('[Analytics] GA4 loaded:', measurementId);
}

// Facebook Pixel
function loadFacebookPixel(pixelId) {
  if (window.fbPixelLoaded) return;
  window.fbPixelLoaded = true;

  const script = document.createElement('script');
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(script);

  // Noscript fallback
  const noscript = document.createElement('noscript');
  noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1"/>`;
  document.body.appendChild(noscript);

  console.log('[Analytics] Facebook Pixel loaded:', pixelId);
}

// Google Ads
function loadGoogleAds(adsId) {
  if (window.gadsLoaded) return;
  window.gadsLoaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${adsId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag(){window.dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', adsId);

  console.log('[Analytics] Google Ads loaded:', adsId);
}

// LinkedIn Insight Tag
function loadLinkedInInsight(partnerId) {
  if (window.linkedinLoaded) return;
  window.linkedinLoaded = true;

  const script = document.createElement('script');
  script.innerHTML = `
    _linkedin_partner_id = "${partnerId}";
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(_linkedin_partner_id);
  `;
  document.head.appendChild(script);

  const script2 = document.createElement('script');
  script2.innerHTML = `
    (function(l) {
    if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
    window.lintrk.q=[]}
    var s = document.getElementsByTagName("script")[0];
    var b = document.createElement("script");
    b.type = "text/javascript";b.async = true;
    b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
    s.parentNode.insertBefore(b, s);})(window.lintrk);
  `;
  document.head.appendChild(script2);

  // Noscript fallback
  const noscript = document.createElement('noscript');
  noscript.innerHTML = `<img height="1" width="1" style="display:none;" alt="" src="https://px.ads.linkedin.com/collect/?pid=${partnerId}&fmt=gif" />`;
  document.body.appendChild(noscript);

  console.log('[Analytics] LinkedIn Insight loaded:', partnerId);
}

// Custom Script Injection
function injectCustomScript(scriptContent, location) {
  if (!scriptContent || !scriptContent.trim()) return;
  
  const container = document.createElement('div');
  container.innerHTML = scriptContent;
  
  // Extract and execute scripts
  const scripts = container.getElementsByTagName('script');
  const target = location === 'head' ? document.head : document.body;
  
  Array.from(scripts).forEach(oldScript => {
    const newScript = document.createElement('script');
    
    // Copy attributes
    Array.from(oldScript.attributes).forEach(attr => {
      newScript.setAttribute(attr.name, attr.value);
    });
    
    // Copy content
    if (oldScript.innerHTML) {
      newScript.innerHTML = oldScript.innerHTML;
    }
    
    target.appendChild(newScript);
  });

  // Also append non-script content
  Array.from(container.childNodes).forEach(node => {
    if (node.nodeName !== 'SCRIPT') {
      target.appendChild(node.cloneNode(true));
    }
  });

  console.log(`[Analytics] Custom ${location} scripts injected`);
}

// ==================== TRACKING EVENT HELPERS ====================

/**
 * Track page view across all platforms
 */
export function trackPageView(pageTitle, pageUrl) {
  // GA4
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_title: pageTitle || document.title,
      page_location: pageUrl || window.location.href
    });
  }
  
  // Facebook
  if (window.fbq) {
    window.fbq('track', 'PageView');
  }
}

/**
 * Track product view
 */
export function trackViewProduct(product) {
  // GA4
  if (window.gtag) {
    window.gtag('event', 'view_item', {
      currency: 'INR',
      value: product.price,
      items: [{
        item_id: product.id || product.sku,
        item_name: product.name,
        item_brand: product.brand,
        item_category: product.category,
        price: product.price
      }]
    });
  }
  
  // Facebook
  if (window.fbq) {
    window.fbq('track', 'ViewContent', {
      content_name: product.name,
      content_category: product.category,
      content_ids: [product.id || product.sku],
      content_type: 'product',
      value: product.price,
      currency: 'INR'
    });
  }
}

/**
 * Track add to cart
 */
export function trackAddToCart(product, quantity = 1) {
  // GA4
  if (window.gtag) {
    window.gtag('event', 'add_to_cart', {
      currency: 'INR',
      value: product.price * quantity,
      items: [{
        item_id: product.id || product.sku,
        item_name: product.name,
        item_brand: product.brand,
        item_category: product.category,
        price: product.price,
        quantity: quantity
      }]
    });
  }
  
  // Facebook
  if (window.fbq) {
    window.fbq('track', 'AddToCart', {
      content_name: product.name,
      content_ids: [product.id || product.sku],
      content_type: 'product',
      value: product.price * quantity,
      currency: 'INR'
    });
  }
}

/**
 * Track purchase/conversion
 */
export function trackPurchase(order) {
  // GA4
  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: order.id,
      value: order.total,
      currency: 'INR',
      items: order.items?.map(item => ({
        item_id: item.id || item.sku,
        item_name: item.name,
        item_brand: item.brand,
        price: item.price,
        quantity: item.quantity
      })) || []
    });
  }
  
  // Facebook
  if (window.fbq) {
    window.fbq('track', 'Purchase', {
      content_ids: order.items?.map(i => i.id || i.sku) || [],
      content_type: 'product',
      value: order.total,
      currency: 'INR',
      num_items: order.items?.length || 0
    });
  }
  
  // LinkedIn
  if (window.lintrk) {
    window.lintrk('track', { conversion_id: 'purchase' });
  }
}

/**
 * Track search
 */
export function trackSearch(searchTerm) {
  // GA4
  if (window.gtag) {
    window.gtag('event', 'search', {
      search_term: searchTerm
    });
  }
  
  // Facebook
  if (window.fbq) {
    window.fbq('track', 'Search', {
      search_string: searchTerm
    });
  }
}

/**
 * Track lead/signup
 */
export function trackLead(leadInfo = {}) {
  // GA4
  if (window.gtag) {
    window.gtag('event', 'generate_lead', leadInfo);
  }
  
  // Facebook
  if (window.fbq) {
    window.fbq('track', 'Lead', leadInfo);
  }
  
  // LinkedIn
  if (window.lintrk) {
    window.lintrk('track', { conversion_id: 'lead' });
  }
}

export default AnalyticsManager;
